import { useEffect, useState, useRef, useCallback } from "react";
import "./styles.css";
import { useLocation } from "react-router-dom";
import Header from "../../components/Header/Header";
import NavBar from "../../components/NavBar/NavBar";
import {
  fetchAllTanks,
  fetchAllTanksSensorData,
  fetchUnassignedTanks,
  SensorDataProps,
  TankDataProps,
} from "../../controller/tankController";
import { fetchAllUsers, UserProps } from "../../controller/usersController";
import Footer from "../../components/Footer";
import TankVisualizer from "../../components/TankVisualizer";
import Chart from "../../components/Chart";
import VolumeChart from "../../components/Chart/volumeChart";

const defaultProps = {
  center: {
    lat: 10.99835602,
    lng: 77.01502627,
  },
  zoom: 11,
};

function Dashboard() {
  const [showMenu, setShowMenu] = useState(false);
  const [allTanks, setAllTanks] = useState<TankDataProps[]>([]);
  const [allTanksSensorData, setAllTanksSensorData] = useState<
    SensorDataProps[]
  >([]);
  const [availableTanks, setAllAvailableTanks] = useState<TankDataProps[]>([]);
  const [allUsers, setAllUsers] = useState<UserProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [showLevelLargeGraph, setShowLevelLargeGraph] = useState(false);
  const [showTempLargeGraph, setShowTempLargeGraph] = useState(false);
  const [showBatteryLargeGraph, setShowBatteryLargeGraph] = useState(false);
  const [showVolumeLargeGraph, setShowVolumeLargeGraph] = useState(false);

  // New state for time range control
  const [timeRange, setTimeRange] = useState<"7days" | "24hours">("7days");

  const latestDataRef = useRef({
    tanks: [] as TankDataProps[],
    sensorData: [] as SensorDataProps[],
    availableTanks: [] as TankDataProps[],
    users: [] as UserProps[],
  });

  const location = useLocation();

  const getLatestSensorData = () => {
    if (allTanksSensorData.length === 0) return null;
    return allTanksSensorData[allTanksSensorData.length - 1];
  };

  const getPreviousSensorData = () => {
    if (allTanksSensorData.length < 2) return null;
    return allTanksSensorData[allTanksSensorData.length - 2];
  };


  const hasDataChanged = (newData: any, oldData: any): boolean => {
    if (newData.sensorData.length === 0 && oldData.sensorData.length === 0)
      return false;
    if (newData.sensorData.length !== oldData.sensorData.length) return true;

    const latestNew = newData.sensorData[newData.sensorData.length - 1];
    const latestOld = oldData.sensorData[oldData.sensorData.length - 1];

    if (!latestNew || !latestOld) return true;

    return latestNew.createdAt !== latestOld.createdAt;
  };

  const fetchTanksData = useCallback(
    async (updateState: boolean = false) => {
      try {
        if (updateState && allTanks.length === 0) {
          setIsLoading(true);
        }

        const [
          tankResponse,
          tankSensorDataResponse,
          tanksResponse,
          usersResponse,
        ] = await Promise.all([
          fetchAllTanks(),
          fetchAllTanksSensorData(),
          fetchUnassignedTanks(),
          fetchAllUsers(),
        ]);

        const newData = {
          tanks: tankResponse.data || [],
          sensorData: tankSensorDataResponse.data || [],
          availableTanks: tanksResponse.data || [],
          users: usersResponse.data || [],
        };

        const dataChanged = hasDataChanged(newData, latestDataRef.current);

        latestDataRef.current = newData;

        if (updateState || dataChanged) {
          setAllTanks(newData.tanks);
          setAllTanksSensorData(newData.sensorData);
          setAllAvailableTanks(newData.availableTanks);
          setAllUsers(newData.users);
          setLastUpdateTime(new Date());
        }
      } catch (err: any) {
        console.error("Error fetching tank data:", err.message);
      } finally {
        if (updateState) {
          setIsLoading(false);
        }
      }
    },
    [allTanks.length]
  );

  useEffect(() => {
    window.scrollTo(0, 0);

    fetchTanksData(true);

    const pollingInterval = setInterval(() => {
      fetchTanksData(false);
    }, 1000);

    return () => {
      clearInterval(pollingInterval);
    };
  }, [fetchTanksData, location.pathname]);

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) return "Invalid Date";

      let hours = date.getHours();
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12;
      const minutes = date.getMinutes().toString().padStart(2, "0");

      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();

      return `${hours}:${minutes}${ampm}, ${month} ${day}, ${year}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Date Error";
    }
  };

  const currentLevel =
    isLoading || !getLatestSensorData()
      ? 0
      : Number(getLatestSensorData()?.tankLevel);

  const TANK_HEIGHT = 110.3;
  const RADIUS = 38.3;
  const currentHeight = (currentLevel / 100) * TANK_HEIGHT;
  const currentVolume = (
    (Math.PI * Math.pow(RADIUS, 2) * currentHeight) /
    1000
  ).toFixed(1);

  const getLatestTankLevelPercent = () => {
    if (isLoading || !getLatestSensorData()) return 0;
    const level = Number(getLatestSensorData()?.tankLevel);
    return level / 100;
  };

  const handleTimeRangeChange = (newTimeRange: "7days" | "24hours") => {
    setTimeRange(newTimeRange);
  };

  return (
    <div className="dashboard-wrapper">
      <NavBar hideNav={showMenu} />
      <div className="dashboard-content">
        <Header
          title="DAQtank Intelligent Fluid Monitoring System"
          setShowMenu={() => setShowMenu(!showMenu)}
          showMenu={showMenu}
        />
        <div className="dashboard-container" id="dashboard-container">
          <div className="dashboard-container-left">
            <div className="company-logos-div">
             
              <div className="logo-right">
                <p className="powered-text">Powered by</p>
                <img
                  src="./images/footer/hub-logo.png"
                  alt="TD4PAI-logo"
                  className="td-logo"
                />
              </div>
            </div>
            <div className="average-container">
              <div className="">
                <TankVisualizer
                  currentLevel={
                    isLoading || !getLatestSensorData()
                      ? 0
                      : Number(getLatestSensorData()?.tankLevel) || 0
                  }
                  history={allTanksSensorData}
                  prevLevel={
                    isLoading || !getPreviousSensorData()
                      ? 0
                      : Number(getPreviousSensorData()?.tankLevel) || 0
                  }
                  address={
                    isLoading ? "Loading" : allUsers && allUsers[0]?.address
                  }
                  tankOrientation={
                    isLoading || !getLatestSensorData()
                      ? "Loading..."
                      : getLatestSensorData()?.tankOrientation || "N/A"
                  }
                  lastUpdated={
                    isLoading || !getLatestSensorData()
                      ? "Loading..."
                      : `${formatDateTime(
                          getLatestSensorData()?.createdAt!
                        )}` || "N/A"
                  }
                  signal="MTN"
                />
              </div>
              <div>
                <h2 className="average-heading-t" style={{ marginTop: "-5%" }}>
                  History
                </h2>
              </div>
              <div className="all-charts">
                <div className="chart-cont">
                  <h2 className="average-heading">Diesel Level</h2>
                  <div className="all-average-cards">
                    <Chart
                      sensorData={allTanksSensorData}
                      dataType="tankLevel"
                      gaugeValue={getLatestTankLevelPercent()}
                      setShowLargeGraph={setShowLevelLargeGraph}
                      showLargeGraph={showLevelLargeGraph}
                      closeLargeGraph={() => setShowLevelLargeGraph(false)}
                      chartTitle="Diesel Level History (%)"
                      timeRange={timeRange}
                      onTimeRangeChange={handleTimeRangeChange}
                    />
                  </div>
                </div>
                <div className="chart-cont">
                  <h2 className="average-heading">Diesel Volume</h2>
                  <div className="all-average-cards">
                    <VolumeChart
                      sensorData={allTanksSensorData}
                      setShowLargeGraph={setShowVolumeLargeGraph}
                      showLargeGraph={showVolumeLargeGraph}
                      closeLargeGraph={() => setShowVolumeLargeGraph(false)}
                      timeRange={timeRange}
                      onTimeRangeChange={handleTimeRangeChange}
                    />
                  </div>
                </div>
                <div className="chart-cont">
                  <h2 className="average-heading">Temperature</h2>
                  <div className="all-average-cards">
                    <Chart
                      sensorData={allTanksSensorData}
                      dataType="tankTemperature"
                      setShowLargeGraph={setShowTempLargeGraph}
                      showLargeGraph={showTempLargeGraph}
                      closeLargeGraph={() => setShowTempLargeGraph(false)}
                      chartTitle="Temperature History (°C)"
                      timeRange={timeRange}
                      onTimeRangeChange={handleTimeRangeChange}
                    />
                  </div>
                </div>
                <div className="chart-cont">
                  <h2 className="average-heading">Battery Level</h2>
                  <div className="all-average-cards">
                    <Chart
                      sensorData={allTanksSensorData}
                      dataType="batteryLevel"
                      setShowLargeGraph={setShowBatteryLargeGraph}
                      showLargeGraph={showBatteryLargeGraph}
                      closeLargeGraph={() => setShowBatteryLargeGraph(false)}
                      chartTitle="Battery Level History (%)"
                      timeRange={timeRange}
                      onTimeRangeChange={handleTimeRangeChange}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{ height: "400px", width: "100%", marginBottom: "15%" }}
            >
              <h2 className="average-heading">Tank Location</h2>
              <div style={{ height: "90%", width: "100%" }}>
                {!isLoading &&
                getLatestSensorData()?.latitude &&
                getLatestSensorData()?.longitude ? (
                  <iframe
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    style={{ border: 0 }}
                    src={`https://maps.google.com/maps?q=8.87952000,
                      7.22756000&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                    allowFullScreen
                  />
                ) : (
                  <iframe
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    style={{ border: 0 }}
                    src={`https://maps.google.com/maps?q=${defaultProps.center.lat},${defaultProps.center.lng}&t=&z=11&ie=UTF8&iwloc=&output=embed`}
                    allowFullScreen
                  />
                )}
                {/* {!isLoading &&
                getLatestSensorData()?.latitude &&
                getLatestSensorData()?.longitude ? (
                  <MapComponent
                    lat={parseFloat(
                      getLatestSensorData()?.latitude || "9.0563"
                    )}
                    lng={parseFloat(
                      getLatestSensorData()?.longitude || "7.4985"
                    )}
                  />
                ) : (
                  <MapComponent lat={9.0563} lng={7.4985} />
                )} */}
              </div>
            </div>
          </div>
          {/* <div className="dashboard-container-right">
            <div className="dashboard-rightTop">
              <h2 className="dashboard-title">Tanks</h2>
              <div className="dashboard-rightTop-cont">
                <div className="bin-stat-div">
                  <div
                    className="bin-statss"
                    style={{
                      backgroundImage: `url('./images/dashboard/bluebg.svg')`,
                      backgroundColor: "#4285F4",
                    }}
                  >
                    <p className="deployed-bins deployed-bin">
                      Assigned Tanks
                      <p className="deployed-no">
                        {isLoading ? "..." : allUsers.length.toString()}
                      </p>
                    </p>
                    <p className="undeployed-bins">
                      Unassigned Tanks
                      <p className="undeployed-no">
                        {isLoading ? "..." : availableTanks.length.toString()}
                      </p>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="dashboard-rightBottom">
              <div className="pickUp-content">
                <h2 className="averagetitle">Users</h2>
                {allUsers.map((data, index) => (
                  <div className="pickUp-info" key={index}>
                    <div className="name-loc">
                      <h3 className="pickUp-name">
                        {isLoading
                          ? "..."
                          : `${data.firstName} ${data.lastName}`}
                      </h3>
                      <p className="pickUp-loc">
                        {isLoading
                          ? "..."
                          : data.address.length > 20
                          ? data.address.slice(0, 20).concat("...")
                          : data.address}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div> */}
        </div>
        <div className="footer-cont">
          <Footer />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;

// [
//   {
//     batteryLevel: "100",
//     createdAt: "2025-05-13T15:55:48.387324+01:00",
//     latitude: "8.490369",
//     longitude: "7.958376",
//     tankDataId: 3666,
//     tankLevel: "27",
//     tankOrientation: "Standing",
//     tankTemperaure: "45",
//     _tank: "TANK0001",
//   },
//   {
//     batteryLevel: "100",
//     createdAt: "2025-05-13T16:00:48.492204+01:00",
//     latitude: "8.490369",
//     longitude: "7.958376",
//     tankDataId: 3667,
//     tankLevel: "27",
//     tankOrientation: "Standing",
//     tankTemperaure: "45",
//     _tank: "TANK0001",
//   },
//   {
//     batteryLevel: "100",
//     createdAt: "2025-05-13T16:05:48.673752+01:00",
//     latitude: "8.490369",
//     longitude: "7.958376",
//     tankDataId: 3668,
//     tankLevel: "27",
//     tankOrientation: "Standing",
//     tankTemperaure: "45",
//     _tank: "TANK0001",
//   },
//   {
//     batteryLevel: "100",
//     createdAt: "2025-05-13T16:10:48.886001+01:00",
//     latitude: "8.490369",
//     longitude: "7.958376",
//     tankDataId: 3669,
//     tankLevel: "27",
//     tankOrientation: "Standing",
//     tankTemperaure: "45",
//     _tank: "TANK0001",
//   },
// ];
